import type { LoginInput, PasswordPolicy, Permission, ProductModule } from '@qualicloud/contracts';
import { createContext, useContext } from 'react';
import type { SessionUser } from '@/lib/api/types';
import { FALLBACK_TIME_ZONE } from '@/lib/format/datetime';

export interface OrganizationContextInfo {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  /** Fuso usado em TODA exibição de data regulada. */
  readonly timeZone: string;
  readonly passwordPolicy: PasswordPolicy;
  readonly allowSelfApproval: boolean;
  readonly documentExpiryWarningDays: number;
  readonly recordRetentionYears: number;
  readonly part11CertificationLetterSentAt: string | null;
}

export interface AuthState {
  readonly status: 'booting' | 'anonymous' | 'authenticated';
  readonly user: SessionUser | null;
  readonly permissions: ReadonlySet<Permission>;
  /**
   * Os módulos que a ORGANIZAÇÃO contratou, vindos de `/auth/me`.
   *
   * Servem para DESENHAR — esconder do menu o que a empresa não tem. Não são
   * controle: quem bloqueia de verdade é o gateway, que devolve 402 para rota
   * de módulo não contratado, decidindo pelo claim assinado do token e não por
   * este conjunto.
   *
   * A distinção importa porque as duas fontes podem divergir por até um TTL de
   * token depois de uma mudança de contrato. E ela falha para o lado certo: a
   * tela pode aparecer antes de a rota liberar — o usuário vê o 402 e reclama —,
   * nunca a rota liberar sem a tela existir.
   */
  readonly modules: ReadonlySet<ProductModule>;
  readonly organization: OrganizationContextInfo | null;
  /**
   * Sessão de PLATFORM_ADMIN operando dentro da organização de um cliente.
   * Enquanto verdadeiro, a interface exibe aviso permanente e todo acesso gera
   * evento `VENDOR_ACCESS` (plano §6.5).
   */
  readonly vendorAccess: boolean;
}

export interface AuthContextValue extends AuthState {
  readonly login: (input: LoginInput) => Promise<SessionUser>;
  readonly logout: () => Promise<void>;
  readonly refreshProfile: () => Promise<void>;
  readonly can: (permission: Permission) => boolean;
  /** A organização contratou este módulo? Conveniência de tela, não autorização. */
  readonly hasModule: (module: ProductModule) => boolean;
  readonly canAny: (...permissions: readonly Permission[]) => boolean;
  readonly setMustChangePassword: (value: boolean) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * O contexto é exportado separadamente do provedor para que os testes possam
 * injetar uma sessão determinística sem executar `bootstrapSession()`, que faz
 * requisição de rede na montagem.
 */
export const AuthContextForTests = AuthContext;

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return ctx;
}

/** Fuso da organização, com queda para o padrão enquanto não há sessão. */
export function useOrganizationTimeZone(): string {
  const ctx = useContext(AuthContext);
  return ctx?.organization?.timeZone ?? FALLBACK_TIME_ZONE;
}
